import React, { useEffect, useMemo, useState, useRef } from "react"
import { useConsent } from "./consentprovider"
import { generateDefaultConsent, getTCFPurposes } from "../../../lib/consentHelper";
import { Link } from 'gatsby';

import * as styles from "./style.module.css"
import * as formStyles from "../Forms/styles/forms.module.css"
import CloseIcon from "../../images/icons/close.png";

import iabtcflabel from "./iabtcflabel.json"
import config from "../../../config/consent-config";
import ToggleSwitch from "../ToggleSwitch"
import DataRequestForm from "../Forms/dataRequestForm";
import formatTools from "../../../lib/formatTools";
import Negosyo from "../../../lib/negosyo";
import { Dropdown, Message, Segment } from "semantic-ui-react";

const CONSENTPURPOSELIST = getTCFPurposes().consent;
const TOTALVENDORS = config.tcfvendorlist.length + config.tcfnonvendorlist.length;

const ConsentBanner = ({ forceShow, bannerMode = "settings" }) => {
	const { consent, clientRegion, saveConsent, closeConsentBanner } = useConsent()

	// Store comparisons to flags for readability
	const isTCF = clientRegion.type === "TCF";
	const isCPRA = clientRegion.type === "CPRA";
	const isVCDPA = clientRegion.type === "VCDPA";
	const isOthers = clientRegion.type === "";
	// Cannot show TCF official elements if not verified provider
	const isTCFwCMPID = config.IABCMPID > 0 && isTCF;
	//const isTCFwCMPID = isTCF; // Testing

	// Note: Made this OR so that the vendors are still disabled except if it is valid TCF
	const showVendors = (isTCFwCMPID || config.TABS_ENABLED.vendor) && TOTALVENDORS > 0;

	const [local, setLocal] = useState(null)

	const [showFull, setShowFull] = useState(forceShow === true);
	// Current mode in full banner display: "settings" | "data-request"
	const [currentMode, setCurrentMode] = useState(bannerMode);
	const [showAdvanced, setShowAdvanced] = useState(false);

	const [formLoading, setFormLoading] = useState(false);
	const [formError, setFormError] = useState(null);
	const [formCompleted, setFormCompleted] = useState(false);
	const [dataRequestFormValue, setDataRequestFormValue] = useState(null);
	const [dataRequestFormError, setDataRequestFormError] = useState(null);

	const sectionbannerholderRef = useRef(null);
	const sectionbannerspacerRef = useRef(null);

	const {consentTabs, consentCategories} = useMemo(() => {
		let tabs = [];

		if (config.TABS_ENABLED.cookie_settings) {
			tabs.push({ text: "Cookie Settings", value: "settings" })
		}

		if (showVendors) {
			tabs.push({ text: "Vendors", value: "vendors" })
		}

		if (config.TABS_ENABLED.data_request) {
			tabs.push({ text: "Data Request", value: "data-request" })
		}

		let categories = [];

		for (const [key, value] of Object.entries(config.CATEGORIES_ENABLED)) {
			if (value === true) categories.push(key);
		}

		return { consentTabs: tabs, consentCategories: categories };
	}, [])

	useEffect(() => {
		let initialConsent = consent ? consent : generateDefaultConsent(undefined, clientRegion);

		setLocal(initialConsent)
	}, [consent, clientRegion])

	useEffect(() => {
		// This will resize the spacerheight according to consent banner to prevent hiding footer elements

		// Return will destroy observer if any
		if (!local || !sectionbannerholderRef.current) return;

		const observer = new ResizeObserver((entries) => {
			if (!entries || entries.length === 0 || !sectionbannerspacerRef.current) return;

			sectionbannerspacerRef.current.style.height = entries[0].contentRect.height+"px";
		});

		observer.observe(sectionbannerholderRef.current);

		// Clean up the observer when the component unmounts
		return () => observer.disconnect();
	}, [local]);

	/* -----------------------------
		TCF Driven Sync Logic
	----------------------------- */

	function getVendorTCFPurposelist(vendorid, vendorlist) {
		let tmpidx = 0;
		while (tmpidx < vendorlist.length) {
			if (vendorlist[tmpidx].vendorid+""===""+vendorid) {
				return vendorlist[tmpidx].purposeidlist;
			}
			tmpidx++;
		}
		return [];
	}

	function updateVendorAllowed(vendorAttribute, vendorlist, updatedpurpose) {
		let purposeidlist = [];
		for (const vendorId of Object.keys(vendorAttribute)) {
			purposeidlist = getVendorTCFPurposelist(vendorId, vendorlist);
			if (purposeidlist.length > 0) {
				vendorAttribute[vendorId] = purposeidlist.every(pid => updatedpurpose[pid] === true)
			} else {
				vendorAttribute[vendorId] = false;
			}
		}
		return vendorAttribute;
	}

	function setTCFPurposes(map) {
		const newpurpose = {
			...(local.tcf?.purposes || {}),
			...map,
		};

		setLocal(prev => {
			const next = { ...prev };

			// Update TCF purposes ---
			next.tcf = {
				...prev.tcf,
				purposes: newpurpose
			};

			if ("3" in map || "4" in map) {
				// Changed Purpose 3 → Personalized Ads and/or Purpose 4 → Targeted Ads
				const p3 = newpurpose["3"] === true;
				const p4 = newpurpose["4"] === true;

				// Handle only mirror toggles
				next.sharing_opt_out = !p4; // reverse of p4/targeted_advertising
				next.targeted_advertising = p4 ? "granted" : "denied";
				next.ad_personalization = p3 ? "granted" : "denied";
			} // p3 and/or p4



			// Recompute vendor allow/deny ---
			next.tcf.vendors = updateVendorAllowed({ ...prev.tcf.vendors }, config.tcfvendorlist, newpurpose);
			next.nontcf.vendors = updateVendorAllowed({ ...prev.nontcf.vendors }, config.tcfnonvendorlist, newpurpose);

			return next;
		});
	}

	/* -----------------------------
		TCF toggles
	----------------------------- */

	function setVendor(tcfvendor, id, value) {

		const purposeidlist = getVendorTCFPurposelist(
			id,
			tcfvendor ? config.tcfvendorlist : config.tcfnonvendorlist
		);

		if (value) {
			// Verify if vendor allowed to be checked
			const curpurpose = local.tcf?.purposes || {};
			if (!purposeidlist.every(pid => curpurpose[pid] === true)) {
				// Do nothing if not allowed
				return;
			}
		}

		if (tcfvendor) {
			setLocal((prev) => ({
				...prev,
				tcf: {
					...prev.tcf,
					vendors: {
						...(prev.tcf?.vendors || {}),
						[id]: value,
					},
				},
			}))
		} else {
			setLocal((prev) => ({
				...prev,
				nontcf: {
					...prev.nontcf,
					vendors: {
						...(prev.nontcf?.vendors || {}),
						[id]: value,
					},
				},
			}))
		}
		/*
		// Disabled since it's less safe
		if (value) {
			// Enable corresponding cookies required by vendor
			setTCFPurposes(purposeidlist.reduce((out, item) => {
								out[item] = true;
								return out;
							}, {}));
		}
		*/

	}
/*
	// Unused
	function setSpecialFeature(id, value) {
		setLocal((prev) => ({
			...prev,
			tcf: {
				...prev.tcf,
				specialFeatures: {
					...(prev.tcf?.specialFeatures || {}),
					[id]: value,
				},
			},
		}))
	}
*/
	/* -----------------------------
		Consent Management Platform (or Provider) CMP Driven Sync Logic
	----------------------------- */


	/*
	 * EXPERIENCE AND STORAGE
	 */

	function onToggleFunctionalGroup(checked) {
		setLocal((prev) => ({
			...prev,
			functionality_storage: checked ? "granted" : "denied",
			personalization_storage: checked ? "granted" : "denied",
		}))

		setTCFPurposes({ 1: checked, })
	}

	function onToggleFunctionality(checked) {
		setLocal((prev) => ({
			...prev,
			functionality_storage: checked ? "granted" : "denied",
		}))
	}

	function onToggleContentPersonalization(checked) {
		setLocal((prev) => ({
			...prev,
			personalization_storage: checked ? "granted" : "denied",
			profiling:checked ? prev.profiling : "denied" // disabled, set to denied
		}))
	}

	function onToggleProfiling(checked) {
		setLocal((prev) => ({
			...prev,
			profiling: checked ? "granted" : "denied",
		}))
	}

	/*
	 * ANALYTICS AND MEASUREMENT
	 */
	function onToggleAnalytics(checked) {
		setLocal((prev) => ({
			...prev,
			analytics_storage: checked ? "granted" : "denied",
		}))
	}

	function onToggleSensitiveDataLimit(checked) {
		setLocal((prev) => ({
			...prev,
			sensitive_data_limit: checked
		}))
	}

	/*
	 * ADVERTISING AND PRIVACY
	 */

	// For Invalid TCF, CPRA, VCDPA, Others
	function onToggleAdvertisingGroup(checked) {
		if (isOthers) {
			setLocal((prev) => ({
				...prev,
				ad_storage: checked ? "granted" : "denied",
				sale_opt_out: !checked,
				ad_user_data: checked ? "granted" : "denied",
				profiling: checked ? "granted" : "denied",
			}))

			// ad_personalization, targeted_advertising, sharing_opt_out will be handled here
			setTCFPurposes({ 3: checked, 4: checked })
		} else if (isCPRA || isVCDPA) {
			setLocal((prev) => ({
				...prev,
				ad_storage: checked ? "granted" : "denied",
				ad_user_data: checked ? "granted" : "denied",
				profiling: checked ? "granted" : "denied",
			}))

			// ad_personalization will be handled here
			setTCFPurposes({ 3: checked })
		} else if (!isTCFwCMPID) {
			setLocal((prev) => ({
				...prev,
				ad_storage: checked ? "granted" : "denied",
				sale_opt_out: !checked,
				ad_user_data: checked ? "granted" : "denied",
			}))
		}
	}

	function onToggleAds(checked) {
		setLocal((prev) => ({
			...prev,
			ad_storage: checked ? "granted" : "denied",
		}))
	}

	function onTogglePersonalizedAds(checked) {
		// Auto untick sale_opt_out when this checkbox is available (i.e. TCF w/o CMPID)
		setLocal(prev => ({
			...prev,
			sale_opt_out: isTCF?(checked ? false : prev.sale_opt_out):prev.sale_opt_out,
		}))
		// ad_personalization Mirror of TCF P3 - Handle in TCF
		setTCFPurposes({
			3: checked,
		})
	}

	function onToggleTargetedAds(checked) {
		// Auto untick sale_opt_out when this checkbox is available (i.e. TCF w/o CMPID)
		setLocal(prev => ({
			...prev,
			sale_opt_out: isTCF?(checked ? false : prev.sale_opt_out):prev.sale_opt_out,
		}))

		// targeted_advertising Mirror of TCF P4 - Handle in TCF
		setTCFPurposes({
			4: checked,
		})
	}

	function onToggleSaleOptOut(checked) {
		setLocal((prev) => ({
			...prev,
			sale_opt_out: checked,
		}))
	}

	function onToggleAdUserData(checked) {
		setLocal((prev) => ({
			...prev,
			ad_user_data: checked ? "granted" : "denied",
		}));
	}

	function onAdvancedPurposeChange(id, checked) {
		/*
		// No need, handle cascade logic in onSave
		if (id === 3 || id === 4) {
			if (checked) {
				// Only TCF will trigger this, sale_opt_out is hidden, but needs to be set to correct value
				setLocal((prev) => ({
					...prev,
					sale_opt_out: false
				}));
			}
		}
		*/
		setTCFPurposes({ [id]: checked })
	}


	function onAcceptAll() {
		const defaultConsent = generateDefaultConsent(true);

		let newConsent = { ...defaultConsent };

		newConsent = {
			...newConsent,
			sensitive_data_limit: isTCF // Always true for TCF
		}

		saveConsent(newConsent);
	}

	function onRejectAll() {
		const defaultConsent = generateDefaultConsent(false);

		let newConsent = { ...defaultConsent };

		newConsent = {
			...newConsent,
			ads_data_redaction: isTCF || isCPRA // can only be true for TCF and CPRA
		}

		saveConsent(newConsent);
	}

	function onSave() {
		let newConsent = { ...local }

		// Handle Cascade logic
		if (isTCF) {
			// Sale opt out is hidden and auto-toggled
			newConsent.sale_opt_out = local.targeted_advertising === "denied" && local.ad_personalization === "denied";
		}

		if (newConsent.sale_opt_out) {
			newConsent.ad_personalization = "denied";
			newConsent.targeted_advertising = "denied";
			newConsent.sharing_opt_out = true; // opposite of targeted_advertising
			newConsent.profiling = "denied";

			if (newConsent.tcf) {
				if (newConsent.tcf.purposes) {
					newConsent.tcf.purposes[3] = false;
					newConsent.tcf.purposes[4] = false;

					// Recompute vendor allow/deny ---
					newConsent.tcf.vendors = updateVendorAllowed({ ...local.tcf.vendors }, config.tcfvendorlist, newConsent.tcf.purposes);
					newConsent.nontcf.vendors = updateVendorAllowed({ ...local.nontcf.vendors }, config.tcfnonvendorlist, newConsent.tcf.purposes);

				}
			}
		}

		if (newConsent.personalization_storage === "denied") {
			newConsent.profiling = "denied";
		}

		if (newConsent.targeted_advertising === "denied" || newConsent.ad_personalization === "denied") {
			newConsent.ad_user_data = "denied";
		}

		newConsent = {
			...newConsent,
			sensitive_data_limit: isTCF ? true: local.sensitive_data_limit, // always true for TCF
			ads_data_redaction: (isTCF || isCPRA) && newConsent.ad_storage !== "granted"
		}

		saveConsent(newConsent)
	}

	function displayVendor(vendorobj, tcfvendor) {
		return <div key={vendorobj.vendorid}>
			<ToggleSwitch
				checked={tcfvendor ? local.tcf?.vendors?.[vendorobj.vendorid] === true : local.nontcf?.vendors?.[vendorobj.vendorid] === true}
				onChange={e => setVendor(tcfvendor, vendorobj.vendorid, e.target.checked)}
				label={`${tcfvendor ? vendorobj.name + " [TCF Vendor ID " + vendorobj.vendorid + "]" : vendorobj.name}`}
			/>
			<div style={{ marginLeft: "2em" }}>

				{vendorobj.purposeidlist.map(pid => (
					CONSENTPURPOSELIST.includes(pid)
						? <ToggleSwitch
							key={vendorobj.vendorid + pid}
							checked={local.tcf?.purposes[pid]}
							disabled={true}
							label={`${iabtcflabel.purposes["" + pid]} [TCF Purpose ${pid}]`}
						/>
						: <ToggleSwitch
							key={vendorobj.vendorid + pid}
							disabled={true}
							label={`${iabtcflabel.purposes["" + pid]} [TCF Purpose ${pid}]`}
							hidden={true}
						/>
				))}
			</div>
			<br />
		</div>
	}

	function displayTCFPurpose(pid, disabled, additionallabel = "") {
		const checked = local.tcf?.purposes?.[pid] === true;
		if (!CONSENTPURPOSELIST.includes(pid)) {
			return <></>
		}
		return <ToggleSwitch
			disabled={disabled}
			checked={checked}
			onChange={e => onAdvancedPurposeChange(pid, e.target.checked)}
			label={`
				${additionallabel.length > 0 ? additionallabel + " / " : ""}
				${iabtcflabel.purposes[`${pid}`]} [TCF Purpose ${pid}]
			`}
		/>
	}

	const handleChangeMode = ( e, { value }) => {
		if (formLoading) return;

		setCurrentMode(value);

		const bannerContent = document.getElementById("banner-content");
		if (bannerContent) {
			bannerContent.scrollTop = 0;
		}

		if (value === "data-request") {
			setFormCompleted(false);
		}
	}

	const handleClose = () => {
		if (formLoading) return;

		setCurrentMode("settings")
		if (forceShow) {
			// Cookie Settings
			closeConsentBanner();
		} else {
			// Default, initial
			setShowFull(false);
		}
	}

	function onKeyDownAccessibility(e, callback) {
		if (e.key === "Enter" || e.key === ' ') {
			e.preventDefault();
			callback();
		}
	}

	const handleSubmitDataRequest = async () => {
		const {
			name,
			email,
			contact,
			proofOfIdentity,
			recentOrderTransaction,
			requestType,
			requestDetails,
		} = dataRequestFormValue;

		const formErrors = {};

		if (name.trim().length === 0) {
			formErrors.name = "Required";
		}

		if (email.trim().length === 0) {
			formErrors.email = "Required";
		} else if (!formatTools.validateEmail(email)) {
			formErrors.email = "Must be a valid email address";
		}

		if (contact.trim().length === 0) {
			formErrors.contact = "Required";
		} else if (!formatTools.validateMobile(contact)) {
			formErrors.contact = "Must be a valid mobile number";
		}

		if (proofOfIdentity === undefined) {
			formErrors.proofOfIdentity = "Required";
		}

		if (recentOrderTransaction.trim().length === 0) {
			formErrors.recentOrderTransaction = "Required";
		}

		if (requestType.length === 0) {
			formErrors.requestType = "Required";
		}

		if (requestDetails.trim().length === 0) {
			formErrors.requestDetails = "Required";
		}

		if (Object.keys(formErrors).length > 0) {
			setDataRequestFormError(formErrors);
			return;
		}

		try {
			setFormError(null);
			setFormLoading(true);

			const submitData = [
				{ label: "Full Name", value: name},
				{ label: "Email Address", value: email},
				{ label: "Phone Number", value: contact},
				{ label: "Proof of Identity", value: proofOfIdentity},
				{ label: "Recent Order # or Transaction #", value: recentOrderTransaction},
				{ label: "Type of Request", value: requestType},
				{ label: "Request Details", value: requestDetails},
			];

			const response = await Negosyo.sendInquiry("datarequest", submitData);

			if (response.status === "OK") {
				setFormLoading(false);
				setFormCompleted(true);
            } else {
				setFormLoading(false);
				setFormError("Unable to send request.")
				document.getElementById("form-error").scrollIntoView({ behavior: 'smooth' })
			}
		} catch (error) {
			setFormLoading(false);
			setFormError("Error occured, please try again.")
			document.getElementById("form-error").scrollIntoView({ behavior: 'smooth' })
		}
	}

	if (!local) return null

	const essentialonly = consentCategories.length < 1
								|| (consentCategories.length === 1 && consentCategories.includes("essential"));

	return (<>
	{showFull && <div className={styles.consentBannerOverlay} />}
	{<div style={{"display":showFull?"none":"inline-block", "padding":"0px", "margin":"0px", "width":"100%"}} ref={sectionbannerspacerRef}></div>}
	<div
		ref={sectionbannerholderRef}
		className={`${styles.banner} ${!showFull ? styles.minimal : ''}`}
		name="consentBanner"
		role="dialog"
		aria-modal="true"
		aria-labelledby="consent-banner-title"
		aria-describedby="consent-banner-desc"
		tabIndex={0}
	>
		{showFull &&
			<div className={styles.header}>
				{consentTabs.length === 1
					? <h2>{consentTabs[0].text}</h2>
					: <Dropdown
						fluid
						selection
						value={currentMode}
						options={consentTabs}
						selectOnBlur={false}
						selectOnNavigation={false}
						onChange={handleChangeMode}
						className={styles.dropdown}
						open={formLoading ? false : undefined}
					/>
				}
				<img
					src={CloseIcon}
					alt="close consent banner"
					className={styles.close}
					name="closeConsentButton"
					onClick={handleClose}
					onKeyDown={(e) => onKeyDownAccessibility(e, handleClose)}
					role="button"
					tabIndex={0}
				/>
			</div>
		}

		<div className={styles.content} id="banner-content">
			{!showFull &&
				<>
					<div>
						<h2 id="consent-banner-title">We value your privacy</h2>
						<p className={styles.consentBannerDesc} id="consent-banner-desc">
							{ essentialonly ?
								<>
									This website uses only essential cookies required for basic functionality. No analytics, advertising, or tracking cookies are used.
								</>
							:
								<>
									We
									{showVendors && (
										<> and our <Link to="" onClick={(e) => {
											e.preventDefault();
											setShowFull(true);
											setCurrentMode("vendors")
										}}>{TOTALVENDORS} vendors</Link></>
									)}
									{` use cookies and similar technologies to `}{consentCategories.map((categorykey, categorykeyidx)=>{
										let prefix = "";
										let curtext = "";
										if (categorykeyidx > 0) {
											if (categorykeyidx === consentCategories.length - 1) {
												prefix = " and ";
											} else {
												prefix = ", ";
											}
										}
										if (categorykey === "essential") {
											curtext = "operate the site";
										} else if (categorykey === "storage") {
											curtext = "personalize content";
										} else if (categorykey === "analytics") {
											curtext = "analyze traffic";
										} else if (categorykey === "ads") {
											curtext = "measure ads";
										}
										return <React.Fragment key={categorykeyidx}>{prefix+curtext}</React.Fragment>;
									})}. {!consentCategories.includes("ads")&&<>
										No advertising cookies are used.
									</>} You can accept, reject, or customize your choices.
								</>
							}  Refer to our <Link onClick={handleClose} to={"/cookie"}>Cookie Policy</Link> for details.

							{(isCPRA || isVCDPA) && <>
								<br /><br />
								<Link to="" onClick={(e) => {
									e.preventDefault();
									onToggleSaleOptOut(true); // sale_opt_out = true
									onToggleTargetedAds(false); // sharing_opt_out = true
									onToggleAds(false); // ad_storage = false

									setShowFull(true);
									setCurrentMode("settings");

									requestAnimationFrame(() => {
										const bannerContent = document.getElementById("banner-content");
										if (bannerContent) {
											bannerContent.scrollTo({
												top: bannerContent.scrollHeight - bannerContent.clientHeight,
												behavior: "smooth"
											});
										}
									})
								}}>Do Not Sell/Share</Link>

								{isCPRA && <>
									&nbsp;&nbsp;
									<Link to="" onClick={(e) => {
										e.preventDefault();
										onToggleSensitiveDataLimit(true); // sensitive_data_limit = true

										setShowFull(true);
										setCurrentMode("settings");

										requestAnimationFrame(() => {
											const bannerContent = document.getElementById("banner-content");
											if (bannerContent) {
												bannerContent.scrollTo({
													top: bannerContent.scrollHeight - bannerContent.clientHeight,
													behavior: "smooth"
												});
											}
										})
									}}>Limit Sensitive Info</Link>
								</>}
							</>}
						</p>
					</div>
				</>
			}

			{(showFull && currentMode === "settings") &&
				<>
					<p id="consent-banner-desc">
						Choose how we use cookies and similar technologies.
						You can change your choices at any time.
						Refer to our <Link onClick={handleClose} to={"/cookie"}>Cookie Policy</Link> for details.
					</p>

					{/*
					  * ESSENTIAL / NECESSARY (Required)
					  * - security_storage
					  */}
					<div className={styles.consentGroup}>
						<div className={styles.groupHeader}>
							<h3>Essential / Necessary</h3>
						</div>
						<div className={styles.groupContent}>
							<p>Required for the website to operate safely and correctly.</p>
							<ul>
								<li>Basic functionality such as service delivery, page loading and navigation.</li>
								<li>Shopping Cart Functionality.</li>
								<li>Support login, security, fraud prevention, and system stability</li>
							</ul>
							<p>These do not involve advertising or cross-site tracking.</p>

							<ToggleSwitch checked={true} disabled={true} label="Always Allowed" />
						</div>
					</div>

					{/*
					  * EXPERIENCE AND STORAGE
					  * - functionality_storage
					  * - personalization_storage
					  * - TCF Purpose 1
					  */}
					{consentCategories.includes("storage") && (
					<div className={styles.consentGroup}>
						<div className={styles.groupHeader}>
							<h3>Experience and Storage</h3>
						</div>

						<div className={styles.groupContent}>
							{(isCPRA || isVCDPA || isOthers) && <>
								<p>Enable features that remember your preferences and improve usability.</p>
								<ul>
									<li>Store settings such as language, layout, and preferences.</li>
									<li>Keep you logged in and maintain session state.</li>
									<li>Provide a consistent and personalized experience.</li>
								</ul>
								<p>These do not track your activity across other websites.</p>

								<ToggleSwitch
									checked={local.functionality_storage === "granted"}
									onChange={(e) => onToggleFunctionalGroup(e.target.checked)}
									label="Enable essential features and remember your preferences."
								/>
							</>}

							{isTCF && <>
								<p>Enable essential features and remember your preferences.</p>
								<ToggleSwitch
									checked={local.functionality_storage === "granted"}
									onChange={e => onToggleFunctionality(e.target.checked)}
									label="Enable essential features like language preferences, secure login, and cart persistence."
								/>

								<ToggleSwitch
									checked={local.personalization_storage === "granted"}
									onChange={e => onToggleContentPersonalization(e.target.checked)}
									label="Remember your preferences show you content more relevant to your interests."
								/>

								{isTCFwCMPID && <>
									{displayTCFPurpose(1, false, "")}
								</>}
							</>}
						</div>
					</div>
					)}

					{/*
					  * ANALYTICS & MEASUREMENT
					  * - analytics_storage
					  * - sensitive_data_limit
					  * - TCF Purpose 7
					  */}
					{consentCategories.includes("analytics") && (
					<div className={styles.consentGroup}>
						<div className={styles.groupHeader}>
							<h3>Analytics and Measurement</h3>
						</div>
						<div className={styles.groupContent}>
							<p>Improve site performance and understand how the site is used.</p>

							<ToggleSwitch
								checked={local.analytics_storage === "granted"}
								onChange={e => onToggleAnalytics(e.target.checked)}
								label="Allow us to monitor site traffic and performance to improve the user experience."
							/>

							{(isCPRA || isVCDPA || isOthers) && <>
								<ToggleSwitch
									checked={local.sensitive_data_limit === true}
									onChange={e => onToggleSensitiveDataLimit(e.target.checked)}
									label="Restrict the use of sensitive personal information such as precise location or health-related browsing data for profiling."
								/>
							</>}

							{isTCFwCMPID && <>
								{displayTCFPurpose(7, false, "")}
							</>}
						</div>
					</div>
					)}

					{/*
					  * ADVERTISING / PRIVACY
					  * - ad_storage
					  * - sale_opt_out
					  * - ad_user_data
					  * - ad_personalization / TCF Purpose 3
					  * - targeted_advertising / TCF Purpose 4 / sharing_opt_out
					  * - profiling
					  */}
					{consentCategories.includes("ads") && (
					<div className={styles.consentGroup}>
						<div className={styles.groupHeader}>
							<h3>Advertising and Privacy</h3>
						</div>

						<div className={styles.groupContent}>
							{isOthers && <>
								<p>Support advertising functions and give you control over how your data is used.</p>
								<ul>
									<li>Allow ads to display and function correctly.</li>
									<li>Limit how often you see the same ad.</li>
									<li>Measure ad performance and effectiveness.</li>
									<li>Personalize ads based on your activity.</li>
								</ul>

								<ToggleSwitch
									checked={local.ad_storage === "granted"}
									onChange={(e) => onToggleAdvertisingGroup(e.target.checked)}
									label="Allow ads, personalized content, and advertising across different websites."
								/>
							</>}

							{(isTCF || isCPRA || isVCDPA) && <>
								<p>Support ads, personalize content, and control targeted advertising.</p>

								{((isTCF && !isTCFwCMPID) || isCPRA || isVCDPA) && <>
									<ToggleSwitch
										checked={local.ad_storage === "granted"}
										onChange={e => onToggleAdvertisingGroup(e.target.checked)}
										label={"Allow ads and personalized content."}
									/>
								</>}

								{(isCPRA || isVCDPA) && <>
									{/* CCPA - Do Not Sell / Share Personal Information */}
									<ToggleSwitch
										checked={local.sale_opt_out === true}
										onChange={e => onToggleSaleOptOut(e.target.checked)}
										label="Do Not Sell My Personal Info"
									/>
								</>}

								{isTCFwCMPID && <>
									<ToggleSwitch
										checked={local.ad_storage === "granted"}
										onChange={e => onToggleAds(e.target.checked)}
										label="Allow the storage of data needed to serve and manage digital advertisements."
									/>
								</>}

								{isTCF && <>
									<ToggleSwitch
										disabled={local.personalization_storage === "denied" || local.sale_opt_out === true}
										checked={local.profiling === "granted"}
										onChange={e => onToggleProfiling(e.target.checked)}
										label="Allow automated processing to evaluate or predict your behavior or preferences."
									/>
								</>}

								{isCPRA && <>
									<ToggleSwitch
										disabled={local.sale_opt_out === true}
										checked={local.tcf?.purposes?.[4] !== true}
										onChange={e => onToggleTargetedAds(!e.target.checked)}
										label={"Do Not Share My Personal Information (Cross-context Behavioral Advertising)"}
									/>
								</>}


								{isTCF && <>
									{/* Personalized Ads - within Site */}
									{isTCFwCMPID
										? displayTCFPurpose(3, false, "")
										: <ToggleSwitch
											disabled={local.sale_opt_out === true}
											checked={local.tcf?.purposes?.[3] === true}
											onChange={e => onTogglePersonalizedAds(e.target.checked)}
											label="Allow the use of your data to show you ads that are more relevant to you."
										/>
									}
								</>}

								{(isTCF || isVCDPA) && <>
									{/* Targeted Ads - usually Cross Site */}
									{isTCFwCMPID
										? displayTCFPurpose(4, false, "")
										: <ToggleSwitch
											disabled={local.sale_opt_out === true && !isTCF}
											checked={local.tcf?.purposes?.[4] === true}
											onChange={e => onToggleTargetedAds(e.target.checked)}
											label={"Allow your activity data to be used to display ads across different websites and apps."}
										/>
									}
								</>}

								{isTCFwCMPID && <>
									<ToggleSwitch
										disabled={local.tcf?.purposes?.[3] === false || local.tcf?.purposes?.[4] === false || local.sale_opt_out === true}
										checked={local.ad_user_data === "granted"}
										onChange={e => onToggleAdUserData(e.target.checked)}
										label="Allow sending user data to advertising partners to measure ad performance."
									/>
								</>}

							</>}

							{showVendors && <>
								{/* Advanced TCF */}
								<br />
								<p
									className={styles.buttonText}
									onClick={() => handleChangeMode(undefined, { value: "vendors" })}
									onKeyDown={(e) => onKeyDownAccessibility(e, () => handleChangeMode(undefined, { value: "vendors" }))}
									role="button"
									tabIndex={0}
								>
									Show Vendor Settings
								</p>
								<br />
							</>}
						</div>
						{isTCFwCMPID && CONSENTPURPOSELIST.length > 0 && <>
							<br />
							<p>
								TCF Purposes are defined by the Interactive Advertising Bureau (IAB)
								Europe Transparency & Consent Framework (TCF) as a standardized set of
								data-processing purposes that ensure consistent disclosure, user choice,
								and interoperability between publishers, CMPs, and advertising vendors.
							</p>
						</>}
					</div>
					)}
				</>
			}

			{(showFull && currentMode === "vendors") && <>
				{/* {isTCFwCMPID ? <></> : <></>} */}
				<div>
					<h3>Vendors</h3>

					{config.tcfvendorlist.map(vendorobj => (
						displayVendor(vendorobj, true)
					))}
					{config.tcfnonvendorlist.map(vendorobj => (
						displayVendor(vendorobj, false)
					))}
				</div>
			</>}

			{(showFull && currentMode === "data-request") &&
				<div className={`${styles.dataRequest} ${formStyles.formShortened}`}>
					{formCompleted
						? <>
							<Message positive className={`${styles.message}`}>
								<Message.Header>Thank you for your request.</Message.Header>
								<br />
								A representative wiill get in touch with you soon.
							</Message>
						</>
						: <>
							<p id="consent-banner-desc">
								Please complete this form to exercise your rights regarding your personal data.
								We will respond to your request within <b>thirty (30 days)</b> of receipt and
								verification of your identity.
							</p>

							<DataRequestForm
								handleFormValueChange={(val) => {
									setDataRequestFormValue(val);
									setFormError(null);
								}}
								error={dataRequestFormError}
								loading={formLoading}
							/>

							{formError && <>
								<Message id="form-error" negative className={`${styles.centerText}`}>
									<Message.Header>
										{formError}
									</Message.Header>
								</Message>
							</>}

							<br />
							<Segment>
								<p><b>Data Protection Officer Details</b></p>
								<p>
									<span>Email: <a href="mailto:dpo@negosyonow.com">dpo@negosyonow.com</a></span>
									<br />
									<span>Address: 704 Rizal Ave. ext., Caloocan City, Metro Manila</span>
								</p>
							</Segment>

						</>
					}
				</div>
			}
		</div>

		{/* ACTION BUTTONS */}
		<div className={styles.actionList}>
			{essentialonly && <>
				<button
					onClick={onRejectAll}
					name="rejectAllConsentButton"
					role="button"
					aria-label="OK"
				>
					OK
				</button>
			</>}
			{(currentMode === "settings" || currentMode === "vendors") && (!essentialonly) && <>
				{!showFull &&
					<button
						className={styles.secondary} onClick={() => {
							setShowFull(true);
							setCurrentMode("settings");
						}}
						name="customizeConsentButton"
						role="button"
						aria-label="Customize"
						aria-haspopup="dialog"
					>
						Customize
					</button>
				}
				<button
					onClick={onRejectAll}
					name="rejectAllConsentButton"
					role="button"
					aria-label="Reject All"
				>
					Reject All
				</button>
				{showFull &&
					<button
						onClick={onSave}
						name="savePreferencesConsentButton"
						role="button"
						aria-label="Save Preferences"
					>
						Save Preferences
					</button>
				}
				<button
					onClick={onAcceptAll}
					name="acceptAllConsentButton"
					role="button"
					aria-label="Accept All"
				>
					Accept All
				</button>
			</>}

			{(showFull && currentMode === "data-request") && <>
				<button
					onClick={(formLoading || formCompleted) ? null : handleSubmitDataRequest}
					name="submitDataRequestButton"
					role="button"
					disabled={formLoading || formCompleted}
					aria-label="Submit Request"
				>
					Submit Request
				</button>
			</>}
		</div>
	</div>
	</>)
}

export default ConsentBanner;
